import {
  createContext,
  useCallback,
  useRef,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";

import type { CreateCompanyParams, VerifyResponse } from "@/services/authService";
import {
  getSession,
  verify as verifyApi,
  verifyRootInvite as verifyRootInviteApi,
  loginWithPassword as loginWithPasswordApi,
  register as registerApi,
  createCompany as createCompanyApi,
  exchangeSsoCode as exchangeSsoCodeApi,
  logout as logoutApi,
  requestLoginLink as requestLoginLinkApi,
  applyVerifyResponse,
  applyImpersonateResponse,
  impersonate as impersonateApi,
  endImpersonation as endImpersonationApi,
  hasStoredToken,
} from "@/services/authService";
import { proactiveSessionRefreshIfDue } from "@/services/apiClient";
import { setAuthenticated as setAnalyticsAuthenticated } from "@/lib/analytics";

export interface AuthUser {
  id: string;
  email: string;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  secondName: string | null;
  secondLastName: string | null;
  documentType: "national_id" | "passport" | null;
  documentNumber: string | null;
  identityVerifiedAt: string | null;
  profileImage: string | null;
  role: string;
  tenantId: string | null;
  tenantName: string | null;
  tenantProfileImage: string | null;
  language: string;
  industry: string | null;
  profession: string | null;
  platformRole: "root" | "root_admin" | null;
  sessionId: string;
  onboardingCompletedAt: string | null;
  onboardingMode: "self" | "accountant" | null;
  planId: string | null;
  hasDgiCredentials: boolean;
  emailVerifiedAt: string | null;
  /**
   * Independent of `role` (which mirrors the active membership). Survives
   * tenant switching, so we can route accountants to /accountant even when
   * they're "in" a client tenant as owner.
   */
  accountType: "business" | "accountant";
}

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  requestLoginLink: (email: string, locale?: string) => Promise<void>;
  verifyAndLogin: (
    token: string,
    activeTenantId?: string
  ) => Promise<void>;
  verifyRootInviteAndLogin: (token: string) => Promise<void>;
  loginWithPassword: (email: string, password: string) => Promise<void>;
  registerAndLogin: (
    email: string,
    password: string,
    registrationToken: string,
    options?: {
      firstName?: string;
      lastName?: string;
      rut?: string;
      razonSocial?: string;
      accountType?: "business" | "accountant";
      partnerCode?: string;
    }
  ) => Promise<{ userId: string }>;
  exchangeSsoCodeAndLogin: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  impersonate: (tenantId: string) => Promise<void>;
  endImpersonation: () => Promise<void>;
  /** Apply select-tenant response (token + user state). Call after selectTenant(tenantId). */
  applySelectTenantResponse: (response: VerifyResponse) => Promise<void>;
  /**
   * Create company from RUT (user with no tenant, or an accountant adding a
   * client). Applies new session and updates user state. Returns the new
   * tenant id so callers can navigate the user into a tenant-scoped URL
   * without waiting for the next render of `user`.
   */
  createCompanyAndSetUser: (
    rut: string,
    companyData?: Omit<CreateCompanyParams, "rut">
  ) => Promise<{ tenantId: string }>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<AuthUser | null>(null);

  // The analytics ingest endpoint is authenticated, so events emitted before
  // login are buffered rather than sent. Flipping this flushes them — they keep
  // their original occurredAt, so pre-login steps are attributed retroactively
  // to whoever completed the sign-in.
  useEffect(() => {
    setAnalyticsAuthenticated(user !== null);
  }, [user]);

  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = useCallback(async (): Promise<void> => {
    if (!hasStoredToken()) {
      setUser(null);
      setIsLoading(false);
      return;
    }
    try {
      const session = await getSession();
      setUser({
        id: session.userId,
        email: session.email ?? "",
        displayName: session.displayName ?? null,
        firstName: session.firstName ?? null,
        lastName: session.lastName ?? null,
        secondName: session.secondName ?? null,
        secondLastName: session.secondLastName ?? null,
        documentType: session.documentType ?? null,
        documentNumber: session.documentNumber ?? null,
        identityVerifiedAt: session.identityVerifiedAt ?? null,
        profileImage: session.profileImage ?? null,
        role: session.role,
        tenantId: session.activeTenantId,
        tenantName: session.tenantName,
        tenantProfileImage: session.tenantProfileImage ?? null,
        language: session.language,
        industry: session.industry,
        profession: session.profession ?? null,
        platformRole: session.platformRole,
        sessionId: session.sessionId,
        onboardingCompletedAt: session.onboardingCompletedAt ?? null,
        onboardingMode: session.onboardingMode ?? null,
        planId: session.planId ?? null,
        hasDgiCredentials: session.hasDgiCredentials ?? false,
        emailVerifiedAt: session.emailVerifiedAt ?? null,
        accountType: session.accountType ?? "business",
      });
    } catch (error) {
      // Say something. This catch swallowing the failure is half of why the
      // permanent-spinner bug survived three debugging sessions: the other
      // half is that a stalled request never rejects at all, so the console
      // stayed empty whether the session call failed or merely hung, and
      // "nothing in the console" read as "nothing happened".
      //
      // Log the two safe fields rather than the error object: an axios error
      // carries config.headers, which the request interceptor has already
      // stamped with `Authorization: Bearer …`, so logging it whole would put
      // the access token in the console and in anything capturing it. `code`
      // and `message` are what a debugger actually needs — a timeout arrives
      // as ECONNABORTED / "timeout of 20000ms exceeded".
      const detail = error as { code?: unknown; message?: unknown };
      console.error("[auth] session bootstrap failed", {
        code: typeof detail?.code === "string" ? detail.code : undefined,
        message:
          typeof detail?.message === "string"
            ? detail.message
            : String(error),
      });
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshUser();
  }, [refreshUser]);

  // Wipe the React Query cache whenever the authenticated identity
  // actually changes (login / logout / impersonate / end-impersonate).
  // Without this, in-memory query data from the previous identity
  // bleeds through for the first paint of the next identity — e.g.
  // accountant A logs out, B logs in on the same page, B briefly sees
  // A's cartera (queryKeys like ["accountant","tenantsSummary"] don't
  // include user id, and staleTime is 60s).
  //
  // Gate on `isLoading` so we never clear during AuthProvider's bootstrap
  // pass on a hard refresh. The initial null→user transition would
  // otherwise call clear() the moment ProtectedRoute unblocks children,
  // wiping the page's just-started useQuery mid-flight and leaving the
  // observer stuck on isLoading=true forever (visible as a permanent
  // spinner on every page except those whose queries gate on user.id).
  const queryClient = useQueryClient();
  const previousUserIdRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (isLoading) return;
    const previous = previousUserIdRef.current;
    const current = user?.id ?? null;
    if (previous === undefined) {
      previousUserIdRef.current = current;
      return;
    }
    if (previous !== current) {
      queryClient.clear();
      previousUserIdRef.current = current;
    }
  }, [user?.id, isLoading, queryClient]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      void proactiveSessionRefreshIfDue();
    }, 60_000);
    const onVisibility = (): void => {
      if (document.visibilityState === "visible") {
        void proactiveSessionRefreshIfDue();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const requestLoginLink = useCallback(
    async (email: string, locale?: string): Promise<void> => {
      await requestLoginLinkApi({ email, locale });
    },
    []
  );

  const verifyAndLogin = useCallback(
    async (token: string, activeTenantId?: string): Promise<void> => {
      const response = await verifyApi({ token, activeTenantId });
      applyVerifyResponse(response);
      const session = await getSession();
      setUser({
        id: session.userId,
        email: response.user.email,
        displayName: response.user.displayName ?? null,
        firstName: response.user.firstName ?? null,
        lastName: response.user.lastName ?? null,
        secondName: session.secondName ?? null,
        secondLastName: session.secondLastName ?? null,
        documentType: session.documentType ?? null,
        documentNumber: session.documentNumber ?? null,
        identityVerifiedAt: session.identityVerifiedAt ?? null,
        profileImage: response.user.profileImage ?? null,
        role: session.role,
        tenantId: session.activeTenantId,
        tenantName: session.tenantName,
        tenantProfileImage: session.tenantProfileImage ?? null,
        language: session.language,
        industry: session.industry,
        profession: session.profession ?? null,
        platformRole: session.platformRole,
        sessionId: session.sessionId,
        onboardingCompletedAt: session.onboardingCompletedAt ?? null,
        onboardingMode: session.onboardingMode ?? null,
        planId: session.planId ?? null,
        hasDgiCredentials: session.hasDgiCredentials ?? false,
        emailVerifiedAt: response.user.emailVerifiedAt ?? session.emailVerifiedAt ?? null,
        accountType: session.accountType ?? "business",
      });
      setIsLoading(false);
    },
    []
  );

  const verifyRootInviteAndLogin = useCallback(
    async (token: string): Promise<void> => {
      const response = await verifyRootInviteApi(token);
      applyVerifyResponse(response);
      const session = await getSession();
      setUser({
        id: session.userId,
        email: response.user.email,
        displayName: response.user.displayName ?? null,
        firstName: response.user.firstName ?? null,
        lastName: response.user.lastName ?? null,
        secondName: session.secondName ?? null,
        secondLastName: session.secondLastName ?? null,
        documentType: session.documentType ?? null,
        documentNumber: session.documentNumber ?? null,
        identityVerifiedAt: session.identityVerifiedAt ?? null,
        profileImage: response.user.profileImage ?? null,
        role: session.role,
        tenantId: session.activeTenantId,
        tenantName: session.tenantName,
        tenantProfileImage: session.tenantProfileImage ?? null,
        language: session.language,
        industry: session.industry,
        profession: session.profession ?? null,
        platformRole: session.platformRole,
        sessionId: session.sessionId,
        onboardingCompletedAt: session.onboardingCompletedAt ?? null,
        onboardingMode: session.onboardingMode ?? null,
        planId: session.planId ?? null,
        hasDgiCredentials: session.hasDgiCredentials ?? false,
        emailVerifiedAt: response.user.emailVerifiedAt ?? session.emailVerifiedAt ?? null,
        accountType: session.accountType ?? "business",
      });
      setIsLoading(false);
    },
    []
  );

  const loginWithPassword = useCallback(
    async (email: string, password: string): Promise<void> => {
      const response = await loginWithPasswordApi({ email, password });
      applyVerifyResponse(response);
      const session = await getSession();
      setUser({
        id: session.userId,
        email: response.user.email,
        displayName: response.user.displayName ?? null,
        firstName: response.user.firstName ?? null,
        lastName: response.user.lastName ?? null,
        secondName: session.secondName ?? null,
        secondLastName: session.secondLastName ?? null,
        documentType: session.documentType ?? null,
        documentNumber: session.documentNumber ?? null,
        identityVerifiedAt: session.identityVerifiedAt ?? null,
        profileImage: response.user.profileImage ?? null,
        role: session.role,
        tenantId: session.activeTenantId,
        tenantName: session.tenantName,
        tenantProfileImage: session.tenantProfileImage ?? null,
        language: session.language,
        industry: session.industry,
        profession: session.profession ?? null,
        platformRole: session.platformRole,
        sessionId: session.sessionId,
        onboardingCompletedAt: session.onboardingCompletedAt ?? null,
        onboardingMode: session.onboardingMode ?? null,
        planId: session.planId ?? null,
        hasDgiCredentials: session.hasDgiCredentials ?? false,
        emailVerifiedAt: response.user.emailVerifiedAt ?? session.emailVerifiedAt ?? null,
        accountType: session.accountType ?? "business",
      });
      setIsLoading(false);
    },
    []
  );

  const registerAndLogin = useCallback(
    async (
      email: string,
      password: string,
      registrationToken: string,
      options?: {
        firstName?: string;
        lastName?: string;
        rut?: string;
        razonSocial?: string;
        accountType?: "business" | "accountant";
        partnerCode?: string;
      }
    ): Promise<{ userId: string }> => {
      const response = await registerApi({
        email,
        ...(options?.firstName ? { firstName: options.firstName } : {}),
        ...(options?.lastName ? { lastName: options.lastName } : {}),
        password,
        registrationToken,
        ...(options?.rut != null && options?.razonSocial != null && {
          rut: options.rut,
          razonSocial: options.razonSocial,
        }),
        ...(options?.accountType ? { accountType: options.accountType } : {}),
        ...(options?.partnerCode ? { partnerCode: options.partnerCode } : {}),
      });
      applyVerifyResponse(response);
      const session = await getSession();
      setUser({
        id: session.userId,
        email: response.user.email,
        displayName: response.user.displayName ?? null,
        firstName: response.user.firstName ?? null,
        lastName: response.user.lastName ?? null,
        secondName: session.secondName ?? null,
        secondLastName: session.secondLastName ?? null,
        documentType: session.documentType ?? null,
        documentNumber: session.documentNumber ?? null,
        identityVerifiedAt: session.identityVerifiedAt ?? null,
        profileImage: response.user.profileImage ?? null,
        role: session.role,
        tenantId: session.activeTenantId,
        tenantName: session.tenantName,
        tenantProfileImage: session.tenantProfileImage ?? null,
        language: session.language,
        industry: session.industry,
        profession: session.profession ?? null,
        platformRole: session.platformRole,
        sessionId: session.sessionId,
        onboardingCompletedAt: session.onboardingCompletedAt ?? null,
        onboardingMode: session.onboardingMode ?? null,
        planId: session.planId ?? null,
        hasDgiCredentials: session.hasDgiCredentials ?? false,
        emailVerifiedAt: response.user.emailVerifiedAt ?? session.emailVerifiedAt ?? null,
        accountType: session.accountType ?? "business",
      });
      setIsLoading(false);
      // Returned so the caller can use it as the conversion transaction_id —
      // session.userId, not response.user.id, to match what every other
      // consumer of this context sees as the user's id.
      return { userId: session.userId };
    },
    []
  );

  const exchangeSsoCodeAndLogin = useCallback(
    async (code: string): Promise<void> => {
      const response = await exchangeSsoCodeApi(code);
      applyVerifyResponse(response);
      const session = await getSession();
      setUser({
        id: session.userId,
        email: response.user.email,
        displayName: response.user.displayName ?? null,
        firstName: response.user.firstName ?? null,
        lastName: response.user.lastName ?? null,
        secondName: session.secondName ?? null,
        secondLastName: session.secondLastName ?? null,
        documentType: session.documentType ?? null,
        documentNumber: session.documentNumber ?? null,
        identityVerifiedAt: session.identityVerifiedAt ?? null,
        profileImage: response.user.profileImage ?? null,
        role: session.role,
        tenantId: session.activeTenantId,
        tenantName: session.tenantName,
        tenantProfileImage: session.tenantProfileImage ?? null,
        language: session.language,
        industry: session.industry,
        profession: session.profession ?? null,
        platformRole: session.platformRole,
        sessionId: session.sessionId,
        onboardingCompletedAt: session.onboardingCompletedAt ?? null,
        onboardingMode: session.onboardingMode ?? null,
        planId: session.planId ?? null,
        hasDgiCredentials: session.hasDgiCredentials ?? false,
        emailVerifiedAt: response.user.emailVerifiedAt ?? session.emailVerifiedAt ?? null,
        accountType: session.accountType ?? "business",
      });
      setIsLoading(false);
    },
    []
  );

  const logout = useCallback(async (): Promise<void> => {
    const sessionId = user?.sessionId;
    try {
      if (sessionId) {
        await logoutApi({ sessionId });
      } else {
        await logoutApi();
      }
    } finally {
      setUser(null);
    }
  }, [user?.sessionId]);

  const impersonate = useCallback(async (tenantId: string): Promise<void> => {
    const response = await impersonateApi(tenantId);
    applyImpersonateResponse(response);
    // Drop the previous tenant's cached queries before the session flips to the
    // new tenant. The user.id-gated clear effect above does NOT fire on an A→B
    // impersonate (same root user, only the active tenant changes), so without
    // this the new tenant renders the old tenant's data under shared query keys
    // until a hard refresh. Mirrors switchTenant's removeQueries().
    queryClient.removeQueries();
    await refreshUser();
  }, [refreshUser, queryClient]);

  const endImpersonation = useCallback(async (): Promise<void> => {
    const response = await endImpersonationApi();
    applyImpersonateResponse(response);
    queryClient.removeQueries();
    await refreshUser();
  }, [refreshUser, queryClient]);

  const applySelectTenantResponse = useCallback(
    async (response: VerifyResponse): Promise<void> => {
      applyVerifyResponse(response);
      const session = await getSession();
      setUser({
        id: session.userId,
        email: response.user.email,
        displayName: response.user.displayName ?? null,
        firstName: response.user.firstName ?? null,
        lastName: response.user.lastName ?? null,
        secondName: session.secondName ?? null,
        secondLastName: session.secondLastName ?? null,
        documentType: session.documentType ?? null,
        documentNumber: session.documentNumber ?? null,
        identityVerifiedAt: session.identityVerifiedAt ?? null,
        profileImage: response.user.profileImage ?? null,
        role: session.role,
        tenantId: session.activeTenantId,
        tenantName: session.tenantName,
        tenantProfileImage: session.tenantProfileImage ?? null,
        language: session.language,
        industry: session.industry,
        profession: session.profession ?? null,
        platformRole: session.platformRole,
        sessionId: session.sessionId,
        onboardingCompletedAt: session.onboardingCompletedAt ?? null,
        onboardingMode: session.onboardingMode ?? null,
        planId: session.planId ?? null,
        hasDgiCredentials: session.hasDgiCredentials ?? false,
        emailVerifiedAt: response.user.emailVerifiedAt ?? session.emailVerifiedAt ?? null,
        accountType: session.accountType ?? "business",
      });
    },
    []
  );

  const createCompanyAndSetUser = useCallback(
    async (
      rut: string,
      companyData?: Omit<CreateCompanyParams, "rut">
    ): Promise<{ tenantId: string }> => {
      const response = await createCompanyApi({ rut, ...companyData });
      applyVerifyResponse(response);
      const session = await getSession();
      setUser({
        id: session.userId,
        email: response.user.email,
        displayName: response.user.displayName ?? null,
        firstName: response.user.firstName ?? null,
        lastName: response.user.lastName ?? null,
        secondName: session.secondName ?? null,
        secondLastName: session.secondLastName ?? null,
        documentType: session.documentType ?? null,
        documentNumber: session.documentNumber ?? null,
        identityVerifiedAt: session.identityVerifiedAt ?? null,
        profileImage: response.user.profileImage ?? null,
        role: session.role,
        tenantId: session.activeTenantId,
        tenantName: session.tenantName,
        tenantProfileImage: session.tenantProfileImage ?? null,
        language: session.language,
        industry: session.industry,
        profession: session.profession ?? null,
        platformRole: session.platformRole,
        sessionId: session.sessionId,
        onboardingCompletedAt: session.onboardingCompletedAt ?? null,
        onboardingMode: session.onboardingMode ?? null,
        planId: session.planId ?? null,
        hasDgiCredentials: session.hasDgiCredentials ?? false,
        emailVerifiedAt: response.user.emailVerifiedAt ?? session.emailVerifiedAt ?? null,
        accountType: session.accountType ?? "business",
      });
      // createCompany on the backend always returns a non-null tenantId
      // (it just created the company + owner membership). Coalesce to the
      // session's activeTenantId for type safety in case the response shape
      // diverges later.
      const tenantId =
        response.user.tenantId ?? session.activeTenantId ?? null;
      if (tenantId == null) {
        throw new Error(
          "createCompany succeeded but the response had no tenant id",
        );
      }
      return { tenantId };
    },
    []
  );

  const value: AuthContextValue = {
    user,
    isLoading,
    isAuthenticated: user != null,
    requestLoginLink,
    verifyAndLogin,
    verifyRootInviteAndLogin,
    loginWithPassword,
    registerAndLogin,
    exchangeSsoCodeAndLogin,
    logout,
    refreshUser,
    impersonate,
    endImpersonation,
    applySelectTenantResponse,
    createCompanyAndSetUser,
  };

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

/**
 * Same as useAuth but returns undefined when used outside AuthProvider.
 * Use only for optional UI (e.g. document title) that should not throw during HMR or before provider mount.
 */
export function useAuthOptional(): AuthContextValue | undefined {
  return useContext(AuthContext);
}
